import { atom } from "jotai";
import { ICheckedForecast } from "store/features";

export interface AnchorDragHistory {
  originalForecast?: Pick<ICheckedForecast, "arps" | "constants">;
  isPreview: boolean;
}

export const activeTypeWellAnchorEditorAtom = atom(null as Nullable<string>);

export interface PendingDragEUR extends AnchorDragHistory {
  /** Primary product name (e.g. "Oil", "Gas") */
  productName: string;
  /** Final EUR in display units (Mbbl / MMcf) — same units shown on the x-axis */
  eur: number;
  /** Id of the type well this drag belongs to — guards against a stale replay
   * after the user switches type wells mid-drag or between mount/unmount. */
  typeWellId: string;
  /** True for the ~30/frame live-preview writes during the gesture; false for
   * the single authoritative write on mouseup. Preview writes must not create
   * undo history entries. */
  isPreview: boolean;
}

/**
 * Atom to store the primary product's end date
 * Used for calculating Cutoff EUR
 */
export const primaryProductEndDateAtom = atom<Date | null>(null);

/**
 * Atom to store EUR values for each product
 * Key: product name (e.g., "Oil", "Gas", "Water")
 * Value: EUR
 *
 */
export const productEURAtom = atom<Record<string, number>>({});

/**
 * Written by useChartTypeWellDrag on drag-commit.
 * TypeWellInputTable watches this atom and, when non-null, calls its own
 * onArpsValueChange(arps, productName, ArpFields.EUR, eur) — the same code
 * path the user triggers by typing in the EUR input field.
 * The input table clears the atom immediately after consuming it.
 */
export const pendingDragEURAtom = atom(null as PendingDragEUR | null);

export interface PendingDragQi extends AnchorDragHistory {
  /** Primary product name (e.g. "Oil", "Gas") */
  productName: string;
  /** New Qi (initial rate) in y-axis display units (bbl/d, Mcf/d, etc.) */
  qi: number;
  /**
   * Target EUR (Mbbl / MMcf) to preserve via Di recalculation.
   * Null if the EUR anchor was not computed.
   */
  eur: Nullable<number>;
  /** Id of the type well this drag belongs to. */
  typeWellId: string;
  /** True for live-preview writes; false for the final mouseup write. */
  isPreview: boolean;
}

/**
 * Written by useChartTypeWellDrag on Qi drag-commit.
 * TypeWellInputTable watches this atom and, when non-null, uses `eur` (if
 * non-null) to recalculate Di via WASM so that EUR is preserved after the Qi
 * change — otherwise falls back to applying Qi only (EUR floats).
 * The input table clears the atom immediately after consuming it.
 */
export const pendingDragQiAtom = atom(null as PendingDragQi | null);

export interface PendingDragB extends AnchorDragHistory {
  /** Primary product name (e.g. "Oil", "Gas") */
  productName: string;
  /** New b-factor (Arps exponent, typically 0–2) */
  b: number;
  /**
   * Which segment the b applies to, as an index into the segments belonging to
   * `productName`, in their existing order. A 3-segment template has more than
   * one editable b (the transition b and the terminal b), so the segment has to
   * be addressed explicitly rather than defaulting to the first decline one.
   */
  segIndex: number;
  /**
   * Target EUR (Mbbl / MMcf) to preserve via Di recalculation.
   * Null if the EUR anchor was not computed.
   */
  eur: Nullable<number>;
  /** Id of the type well this drag belongs to. */
  typeWellId: string;
  /** True for live-preview writes; false for the final mouseup write. */
  isPreview: boolean;
}

/**
 * Written by useChartTypeWellDrag on b-factor drag-commit.
 * TypeWellInputTable watches this atom and, when non-null, uses `eur` (if
 * non-null) to recalculate Di via WASM so that EUR is preserved after the b
 * change — otherwise falls back to applying b only (EUR floats).
 * The input table clears the atom immediately after consuming it.
 */
export const pendingDragBAtom = atom(null as PendingDragB | null);

export interface PendingDragPeak extends AnchorDragHistory {
  /** Primary product name (e.g. "Oil", "Gas") */
  productName: string;
  /** New peak rate: the ramp's terminal rate and the decline's initial rate. */
  qi: number;
  /**
   * Target EUR to preserve by re-solving Di after the peak moves.
   * Null if the EUR anchor was not computed, in which case EUR floats.
   */
  eur: Nullable<number>;
  /** Id of the type well this drag belongs to. */
  typeWellId: string;
  /** True for live-preview writes; false for the final mouseup write. */
  isPreview: boolean;
}

/**
 * Written by useChartTypeWellDrag when the peak anchor is dragged on a ramp-up
 * template. TypeWellInputTable forwards it as ArpFields.Qi, which is the same
 * path a user typing into the Qi field takes, so per-template routing (2-seg vs
 * 3-seg ramp-up) is handled downstream rather than duplicated here.
 * The input table clears the atom immediately after consuming it.
 */
export const pendingDragPeakAtom = atom(null as PendingDragPeak | null);

// bEditableSegments and typeWellTemplateNames moved to the Zustand forecast
// store (src/stores/forecast) — they are durable, per-template data written
// once on template resolution and read cross-tree, not per-frame interaction
// state. See selectBEditableSegments / selectTypeWellTemplateNames.

/**
 * Identifies the chart with an active type-well drag gesture.
 * useTypeWellSeries reads this to bypass the 150 ms debounce so the chart
 * re-renders on every rAF tick during drag (~30 fps) instead of 150 ms after
 * the drag ends.
 */
export const draggingTypeWellChartIdAtom = atom(null as Nullable<string>);
